import axios from "axios";

// Add authentication token to all axios requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses globally for axios
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Helper function to get auth headers for fetch requests
function getAuthHeaders() {
  const token = localStorage.getItem("authToken");
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// Helper function to handle fetch errors
async function handleFetchResponse(response) {
  if (response.status === 401) {
    localStorage.removeItem("authToken");
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

const API_URL = 
  process.env.REACT_APP_API_URL ||
  `${window.location.protocol}//${window.location.hostname}:8000`;

// ============================================
// BALANCE & CATEGORIES
// ============================================

export const fetchLastBalance = async () => {
  const res = await axios.get(`${API_URL}/balance/latest`);
  return res.data;
};

export async function fetchCategories() {
  const response = await axios.get(`${API_URL}/categories`);
  return response.data;
}

export async function fetchBalanceHistory(startDate, endDate, categories = [], limit = 15, offset = 0) {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  if (categories.length > 0) categories.forEach((c) => params.append("categories", c));
  if (!startDate && !endDate) {
    params.append("limit", limit);
    params.append("offset", offset);
  }

  const response = await fetch(
    `${API_URL}/balance/history?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  
  return handleFetchResponse(response);
}

export async function fetchMonthlyBalanceSummary(year) {
  const url = year
    ? `${API_URL}/balance/monthly-summary?year=${year}`
    : `${API_URL}/balance/monthly-summary`;

  const res = await fetch(url, { headers: getAuthHeaders() });
  return handleFetchResponse(res);
}

// ============================================
// INCOME
// ============================================

export const addIncome = async (income) => {
  const res = await axios.post(`${API_URL}/income`, income);
  return res.data;
};

export const fetchIncomeCategories = async () => {
  const res = await axios.get(`${API_URL}/income-categories`);
  return res.data;
};

export async function updateIncome(incomeId, updatedData) {
  const response = await axios.put(`${API_URL}/income/${incomeId}`, updatedData);
  return response.data;
}

export async function deleteIncome(incomeId) {
  const response = await axios.delete(`${API_URL}/income/${incomeId}`);
  return response.data;
}

export const fetchIncomeSummary = async (year, month) => {
  const res = await axios.get(`${API_URL}/income/summary`, {
    params: { year, month },
  });
  return res.data;
};

// ============================================
// EXPENSES
// ============================================

export const addExpense = async (expense) => {
  const res = await axios.post(`${API_URL}/expense`, expense);
  return res.data;
};

export const fetchExpenseCategories = async () => {
  const res = await axios.get(`${API_URL}/expense-categories`);
  return res.data;
};

export async function updateExpense(expenseId, updatedData) {
  const response = await axios.put(`${API_URL}/expense/${expenseId}`, updatedData);
  return response.data;
}

export async function deleteExpense(expenseId) {
  const response = await axios.delete(`${API_URL}/expense/${expenseId}`);
  return response.data;
}

export const fetchExpensesByCategory = async (year, month) => {
  const res = await axios.get(`${API_URL}/expense/by-category`, {
    params: { year, month },
  });
  return res.data;
};

export const fetchExpenseSummary = async (year, month) => {
  const res = await axios.get(`${API_URL}/expense/summary`, {
    params: { year, month },
  });
  return res.data;
};

// ============================================
// INVESTMENTS
// ============================================

export const fetchInvestmentSummary = async (year, month) => {
  const res = await axios.get(`${API_URL}/investment/summary`, {
    params: { year, month },
  });
  return res.data;
};

// ============================================
// BUDGET
// ============================================

export const addBudget = async (budget) => {
  try {
    const res = await axios.post(`${API_URL}/budget`, budget);
    return res.data;
  } catch (err) {
    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }
    throw new Error("Failed to add budget. Please try again.");
  }
};

export const fetchBudgets = async () => {
  const res = await axios.get(`${API_URL}/budget`);
  return res.data;
};

export const updateBudget = async (budgetId, updatedData) => {
  try {
    const res = await axios.put(`${API_URL}/budget/${budgetId}`, updatedData);
    return res.data;
  } catch (err) {
    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }
    throw new Error("Failed to update budget. Please try again.");
  }
};

export const deleteBudget = async (budgetId) => {
  const res = await axios.delete(`${API_URL}/budget/${budgetId}`);
  return res.data;
};

// ============================================
// AUTOMATIC TRANSACTIONS
// ============================================

export const fetchAutoTransactions = async () => {
  const res = await axios.get(`${API_URL}/auto-transaction`);
  return res.data;
};

export const createAutoTransaction = async (autoTransaction) => {
  try {
    const res = await axios.post(`${API_URL}/auto-transaction`, autoTransaction);
    return res.data;
  } catch (err) {
    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }
    throw new Error("Failed to create automatic transaction");
  }
};

export const updateAutoTransaction = async (autoTransactionId, updatedData) => {
  try {
    const res = await axios.put(
      `${API_URL}/auto-transaction/${autoTransactionId}`,
      updatedData
    );
    return res.data;
  } catch (err) {
    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }
    throw new Error("Failed to update automatic transaction");
  }
};

export const deleteAutoTransaction = async (autoTransactionId) => {
  const res = await axios.delete(`${API_URL}/auto-transaction/${autoTransactionId}`);
  return res.data;
};

export const triggerAutoTransactionsNow = async () => {
  try {
    const res = await axios.post(`${API_URL}/auto-transaction/trigger-now`);
    return res.data;
  } catch (err) {
    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }
    throw new Error("Failed to trigger automatic transactions");
  }
};